import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { isMaskedPhone } from '@/features/contact/contactApi'
import { revealSubscriberPhone } from './subscriberApi'

export interface SubscriberPhoneReveal {
  /** Ekranda ko'rsatiladigan qiymat: ochilgan bo'lsa to'liq, aks holda niqob */
  shown: string | null
  /** Raqam OCHILGANMI — `ContactCard.openPhone` uchun ham shu javob */
  revealed: boolean
  /**
   * Raqam NIQOBLANGAN va hali ochilmagan.
   *
   * ⚠️ `useOwnerContact.locked` bilan AYNAN bir xil tushuncha va bir xil
   * sabab: bu holatda qiymatni SAQLASHGA yuborib bo'lmaydi.
   */
  locked: boolean
  canReveal: boolean
  reveal: (reason: string) => void
  revealing: boolean
  revealError: string | null
  /** Ochilgan qiymatni unutish — «Yashirish» */
  hide: () => void
}

/**
 * Abonentning SMS raqamini ochish holati.
 *
 * =====================================================================
 * ⚠️ NEGA MAHALLIY «ko'rsatish» TUGMASI YETMAYDI (2026-08-24)
 * =====================================================================
 * Avval bu yerda oddiy `useState` bor edi: raqam javobda TO'LIQ kelardi
 * va tugma uni faqat EKRANDA ochardi. Endi server `subscriber.phone` ni
 * O'ZI niqoblaydi va ochish uchun alohida yo'l bor
 * (`POST /api/subscribers/{id}/contact/reveal`), ya'ni panelda
 * ochiladigan qiymatning O'ZI yo'q — uni SO'RASH kerak.
 *
 * ⚠️ Shu bilan «panel serverdan qattiqroq bo'lmasin» degan eski dalil
 * ham KUCHINI YO'QOTDI: server endi niqoblaydi va `contact.reveal`
 * talab qiladi, ya'ni tugmani shu huquq ostiga olish panelni
 * qattiqroq emas, server bilan BIR XIL qiladi.
 *
 * <h2>⚠️ KESH YO'Q — ATAYLAB</h2>
 * `useQuery` ISHLATILMAYDI (`fetchVoucherCodes` bilan bir xil sabab):
 * ochilgan raqam faqat shu komponent yashaguncha turadi. Kesh bo'lsa
 * sahifadan chiqib qaytgan odam raqamni yana ko'rardi va BITTA audit
 * yozuvi cheksiz ko'rish huquqiga aylanardi.
 *
 * ⚠️ `enabled` `false` bo'lganda (kartochka yopildi) qiymat DARHOL
 * unutiladi — `useOwnerContact` dagi bir xil effekt.
 */
export function useSubscriberPhoneReveal(
  subscriberId: string,
  storedPhone: string | null,
  enabled = true,
): SubscriberPhoneReveal {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [revealed, setRevealed] = useState<string | null>(null)
  const [revealing, setRevealing] = useState(false)
  const [revealError, setRevealError] = useState<string | null>(null)

  useEffect(() => {
    if (enabled) return
    setRevealed(null)
    setRevealError(null)
  }, [enabled])

  /*
    ⚠️ Boshqa abonentga o'tilganda ochilgan raqam QOLMAYDI. Kartochka
    marshrut parametri bilan almashadi va komponent qayta yaratilmasligi
    mumkin — usiz bir abonentning ochilgan raqami ikkinchisining
    kartochkasida ko'rinib turardi.
  */
  useEffect(() => {
    setRevealed(null)
    setRevealError(null)
  }, [subscriberId])

  const reveal = useCallback(
    (reason: string) => {
      const trimmed = reason.trim()
      // Sababsiz so'rov YUBORILMAYDI — har urinish auditga tushadi
      if (trimmed === '') return
      setRevealing(true)
      setRevealError(null)
      revealSubscriberPhone(subscriberId, trimmed)
        .then((result) => setRevealed(result.phone))
        .catch((error: unknown) => setRevealError(translateError(error, t)))
        .finally(() => setRevealing(false))
    },
    [subscriberId, t],
  )

  const hide = useCallback(() => {
    setRevealed(null)
    setRevealError(null)
  }, [])

  return {
    shown: revealed ?? storedPhone,
    revealed: revealed !== null,
    /*
      ⚠️ Shart SERVER NIQOBIGA qaraydi, «huquqim bormi» ga emas. Backend
      hali niqoblamayotgan bo'lsa (yo'l bosqichma-bosqich chiqyapti)
      qiymat to'liq keladi, `locked` `false` bo'ladi va tahrirlash
      avvalgidek ishlaydi — panel o'zidan to'siq QO'YMAYDI.
    */
    locked: isMaskedPhone(storedPhone) && revealed === null,
    canReveal: can('contact.reveal'),
    reveal,
    revealing,
    revealError,
    hide,
  }
}
